import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Message,
  Modal,
  Radio,
  Space,
  Steps,
  Table,
  Typography,
} from '@arco-design/web-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  syncUpstreamModelsApi,
  syncUpstreamPreviewApi,
  type SyncUpstreamConflict,
  type SyncUpstreamOverwrite,
} from '@/api/models'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { modalWidth } from '@/utils/responsive'

const LOCALE_OPTIONS = [
  { value: 'en', label: 'en', extra: 'English' },
  { value: 'zh-CN', label: 'zh-CN', extra: '简体中文' },
  { value: 'zh-TW', label: 'zh-TW', extra: '繁體中文' },
  { value: 'ja', label: 'ja', extra: '日本語' },
]

const FIELD_LABELS: Record<string, string> = {
  description: '描述',
  icon: '图标',
  tags: '标签',
  vendor: '供应商',
  name_rule: '匹配规则',
  status: '状态',
}

interface Props {
  visible: boolean
  onCancel: () => void
  onSuccess?: () => void
}

function buildOverwrite(
  conflicts: SyncUpstreamConflict[],
  selected: Record<string, Set<string>>
): SyncUpstreamOverwrite[] {
  return conflicts
    .map((item) => ({
      model_name: item.model_name,
      fields: item.fields
        .map((f) => f.field)
        .filter((field) => selected[item.model_name]?.has(field)),
    }))
    .filter((item) => item.fields.length > 0)
}

export default function SyncUpstreamModal({ visible, onCancel, onSuccess }: Props) {
  const { isMobile } = useBreakpoint()
  const qc = useQueryClient()
  const [step, setStep] = useState(0)
  const [locale, setLocale] = useState('zh-CN')
  const [selectedFields, setSelectedFields] = useState<Record<string, Set<string>>>({})

  const previewQuery = useQuery({
    queryKey: ['models-sync-preview', locale],
    queryFn: () => syncUpstreamPreviewApi({ locale }),
    enabled: visible && step === 1,
    staleTime: 0,
  })

  useEffect(() => {
    if (visible) {
      setStep(0)
      setLocale('zh-CN')
      setSelectedFields({})
    }
  }, [visible])

  useEffect(() => {
    const conflicts = previewQuery.data?.conflicts || []
    const next: Record<string, Set<string>> = {}
    for (const item of conflicts) {
      next[item.model_name] = new Set(item.fields.map((f) => f.field))
    }
    setSelectedFields(next)
  }, [previewQuery.data?.conflicts])

  const syncMut = useMutation({
    mutationFn: async () => {
      const preview = previewQuery.data
      const overwrite = buildOverwrite(preview?.conflicts || [], selectedFields)
      return syncUpstreamModelsApi({ locale, overwrite })
    },
    onSuccess: async (data) => {
      Message.success(
        `同步完成：新增模型 ${data.created_models ?? 0}，新增供应商 ${data.created_vendors ?? 0}，更新模型 ${data.updated_models ?? 0}`
      )
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['models-meta'] }),
        qc.invalidateQueries({ queryKey: ['vendors-meta'] }),
        qc.invalidateQueries({ queryKey: ['models-missing'] }),
        qc.invalidateQueries({ queryKey: ['channels'] }),
      ])
      onSuccess?.()
      onCancel()
    },
    onError: (e: Error) => Message.error(e.message || '同步失败'),
  })

  const preview = previewQuery.data
  const conflicts = preview?.conflicts || []

  const conflictRows = useMemo(() => {
    return conflicts.flatMap((item) =>
      item.fields.map((field) => ({
        key: `${item.model_name}-${field.field}`,
        model_name: item.model_name,
        field: field.field,
        local: field.local,
        upstream: field.upstream,
      }))
    )
  }, [conflicts])

  type ConflictRow = (typeof conflictRows)[number]

  const toggleField = (modelName: string, field: string, checked: boolean) => {
    setSelectedFields((prev) => {
      const set = new Set(prev[modelName] || [])
      if (checked) set.add(field)
      else set.delete(field)
      return { ...prev, [modelName]: set }
    })
  }

  const footer = (
    <div className="flex justify-end gap-2">
      {step === 1 && (
        <Button onClick={() => setStep(0)}>上一步</Button>
      )}
      <Button onClick={onCancel}>取消</Button>
      {step === 0 && (
        <Button type="primary" onClick={() => setStep(1)}>
          下一步
        </Button>
      )}
      {step === 1 && (
        <Button
          type="primary"
          loading={syncMut.isPending}
          disabled={previewQuery.isLoading || previewQuery.isError}
          onClick={() => syncMut.mutate()}
        >
          开始同步
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      title="同步上游模型"
      visible={visible}
      onCancel={onCancel}
      footer={footer}
      style={{ width: isMobile ? modalWidth(760) : 760 }}
      unmountOnExit
    >
      <Steps current={step} size="small" className="mb-4">
        <Steps.Step title="选择语言" description="同步元数据语言" />
        <Steps.Step title="预览变更" description="确认后应用" />
      </Steps>

      {step === 0 && (
        <div className="space-y-3">
          <Typography.Text type="secondary">请选择同步语言（影响描述等 i18n 字段）</Typography.Text>
          <Radio.Group value={locale} onChange={setLocale}>
            <Space wrap>
              {LOCALE_OPTIONS.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  {opt.label}
                  <span className="ml-1 text-xs text-gray-400">({opt.extra})</span>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          {previewQuery.isLoading ? (
            <div className="py-10 text-center">
              <Typography.Text type="secondary">正在拉取上游元数据...</Typography.Text>
            </div>
          ) : previewQuery.isError ? (
            <Alert
              type="error"
              content={
                previewQuery.error instanceof Error
                  ? previewQuery.error.message
                  : '同步预览获取失败，请检查网络或上游元数据地址。'
              }
            />
          ) : (
            <>
              <Alert
                type="info"
                content="同步上游只更新模型元数据与供应商信息，不会创建渠道能力，也不会写入模型倍率。"
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Card className="!rounded-xl" bordered>
                  <div className="text-xs text-slate-500">缺失模型（将新增）</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{preview?.missing?.length ?? 0}</div>
                </Card>
                <Card className="!rounded-xl" bordered>
                  <div className="text-xs text-slate-500">字段冲突（可选覆盖）</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{conflicts.length}</div>
                </Card>
              </div>

              {conflictRows.length > 0 ? (
                <Table
                  rowKey="key"
                  size="small"
                  pagination={false}
                  scroll={{ y: 280 }}
                  data={conflictRows}
                  columns={[
                    { title: '模型', dataIndex: 'model_name', width: 180 },
                    {
                      title: '字段',
                      dataIndex: 'field',
                      width: 100,
                      render: (f: string) => FIELD_LABELS[f] || f,
                    },
                    {
                      title: '本地',
                      render: (_: unknown, row: ConflictRow) =>
                        row.local != null ? String(row.local) : '-',
                    },
                    {
                      title: '上游',
                      render: (_: unknown, row: ConflictRow) =>
                        row.upstream != null ? String(row.upstream) : '-',
                    },
                    {
                      title: '覆盖',
                      width: 70,
                      render: (_: unknown, row: ConflictRow) => (
                        <Checkbox
                          checked={selectedFields[row.model_name]?.has(row.field) ?? false}
                          onChange={(checked) => toggleField(row.model_name, row.field, checked)}
                        />
                      ),
                    },
                  ]}
                />
              ) : (
                <Typography.Text type="secondary">无字段冲突，将直接应用新增模型。</Typography.Text>
              )}

              {preview?.source?.models_url && (
                <Typography.Paragraph type="secondary" className="!mb-0 !text-xs break-all">
                  来源：{preview.source.models_url}
                </Typography.Paragraph>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
